import { useId, useMemo, useState } from "react";
import type { Entity, EntityType } from "@/lib/api";
import { cn } from "@/lib/cn";
import { entityIcon, entityTypeLabel } from "@/lib/entityTypes";

/**
 * UI-20 — a typeahead over the organization's entities, for the other end of a relation.
 *
 * A select of every entity stops being usable somewhere past twenty names, which the
 * deployment passed on its first day of curated people. This narrows by what is typed — name or
 * alias — and by the kinds the chosen relation type can join, so the list never offers an
 * entity the Server would refuse. Keyboard: arrows move, Enter picks, Escape closes.
 */
export function EntityPicker({
  entities,
  kinds,
  excludeId,
  value,
  onChange,
  label = "The other entity",
  placeholder = "Type a name…",
}: {
  entities: Entity[];
  /** Only entities of these kinds are offered; undefined offers every kind. */
  kinds?: EntityType[];
  excludeId?: string;
  value: Entity | null;
  onChange: (entity: Entity | null) => void;
  label?: string;
  placeholder?: string;
}) {
  const listId = useId();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);

  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return entities
      .filter((entity) => entity.id !== excludeId && (!kinds || kinds.includes(entity.type)))
      .filter(
        (entity) =>
          needle.length === 0
          || entity.name.toLowerCase().includes(needle)
          || entity.aliases.some((alias) => alias.toLowerCase().includes(needle)),
      )
      .sort((a, b) => a.name.localeCompare(b.name))
      .slice(0, 8);
  }, [entities, excludeId, kinds, query]);

  function pick(entity: Entity) {
    onChange(entity);
    setQuery("");
    setOpen(false);
  }

  if (value) {
    const Icon = entityIcon(value.type);
    return (
      <span className="inline-flex h-8 min-w-0 items-center gap-1.5 rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-2)] pl-2 pr-1 text-[length:var(--text-xs)] text-[var(--text-primary)]">
        <Icon size={12} strokeWidth={2} aria-hidden className="shrink-0 text-[var(--text-muted)]" />
        <span className="truncate">{value.name}</span>
        <button
          type="button"
          aria-label={`Clear ${value.name}`}
          onClick={() => onChange(null)}
          className="flex h-5 w-5 shrink-0 items-center justify-center rounded-[var(--radius-sm)] text-[var(--text-muted)] hover:text-[var(--text-primary)]"
        >
          ×
        </button>
      </span>
    );
  }

  return (
    <div className="relative min-w-[200px] flex-1">
      <input
        role="combobox"
        aria-label={label}
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        value={query}
        placeholder={placeholder}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 120)}
        onChange={(event) => {
          setQuery(event.target.value);
          setActive(0);
          setOpen(true);
        }}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown") {
            event.preventDefault();
            setActive((index) => Math.min(index + 1, matches.length - 1));
          } else if (event.key === "ArrowUp") {
            event.preventDefault();
            setActive((index) => Math.max(index - 1, 0));
          } else if (event.key === "Enter" && matches[active]) {
            event.preventDefault();
            pick(matches[active]!);
          } else if (event.key === "Escape") {
            setOpen(false);
          }
        }}
        className="h-8 w-full rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-2)] px-2.5 text-[length:var(--text-xs)] text-[var(--text-primary)] outline-none focus:border-[var(--ring)]"
      />

      {open && (
        <ul
          id={listId}
          role="listbox"
          className="absolute left-0 right-0 top-[calc(100%+4px)] z-[80] flex max-h-[260px] flex-col overflow-y-auto rounded-[var(--radius-lg)] border border-[var(--border-default)] bg-[var(--surface-3)] p-1 shadow-[var(--shadow-lg)]"
        >
          {matches.length === 0 ? (
            <li className="px-2 py-1.5 text-[length:var(--text-xs)] text-[var(--text-muted)]">
              {kinds ? `No ${kinds.map((kind) => entityTypeLabel(kind).toLowerCase()).join(" or ")} by that name.` : "Nobody by that name."}
            </li>
          ) : (
            matches.map((entity, index) => {
              const Icon = entityIcon(entity.type);
              return (
                <li
                  key={entity.id}
                  role="option"
                  aria-selected={index === active}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    pick(entity);
                  }}
                  onMouseEnter={() => setActive(index)}
                  className={cn(
                    "flex cursor-pointer items-center gap-2 rounded-[var(--radius-sm)] px-2 py-1.5 text-[length:var(--text-xs)]",
                    index === active ? "bg-[var(--surface-2)] text-[var(--text-primary)]" : "text-[var(--text-secondary)]",
                  )}
                >
                  <Icon size={12} strokeWidth={2} aria-hidden className="shrink-0 text-[var(--text-muted)]" />
                  <span className="min-w-0 flex-1 truncate">{entity.name}</span>
                  <span className="shrink-0 text-[length:var(--text-2xs)] text-[var(--text-muted)]">
                    {entityTypeLabel(entity.type)}
                  </span>
                </li>
              );
            })
          )}
        </ul>
      )}
    </div>
  );
}
